import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GoogleLogo, MicrosoftOutlookLogo, Password, LinkBreak, CheckCircle, Plus, Star } from '@phosphor-icons/react';
import {
  fetchIdentities, unlinkIdentity, connectProvider, setPrimaryIdentity,
  type IdentityList, type AuthProvider,
} from '@/lib/authJourney';
import { getApiErrorMessage } from '@/lib/api';

/**
 * Login methods — connected sign-in providers + password status. The canonical place to manage
 * "how you sign in" (Identity domain). Rendered inside the one Account Security page; this is the
 * ONLY implementation of provider connect/disconnect (no duplicate screens — dev principle 6/7).
 */
const PROVIDER_LABEL: Record<string, string> = {
  google: 'Google', microsoft: 'Microsoft', password: 'Password', magic_link: 'Magic link', passkey: 'Passkey', sso: 'SSO',
};
function providerIcon(p: string) {
  if (p === 'google') return <GoogleLogo className="h-5 w-5" weight="bold" />;
  if (p === 'microsoft') return <MicrosoftOutlookLogo className="h-5 w-5" weight="bold" />;
  return <Password className="h-5 w-5" />;
}

export function LoginMethodsSection() {
  const [data, setData] = useState<IdentityList | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try { setData(await fetchIdentities()); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not load your login methods')); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Feedback from the connect-provider redirect (?linked / ?error).
  useEffect(() => {
    const linked = params.get('linked'); const error = params.get('error');
    if (linked) toast.success(`${PROVIDER_LABEL[linked] ?? linked} connected`);
    if (error) toast.error(decodeURIComponent(error));
    if (linked || error) { params.delete('linked'); params.delete('error'); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const credentialCount = (data?.hasPassword ? 1 : 0) + (data?.identities.length ?? 0);
  const connected = new Set(data?.identities.map((i) => i.provider));

  async function handleUnlink(id: string, label: string) {
    setBusy(id);
    try { await unlinkIdentity(id); toast.success(`${label} disconnected`); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not disconnect')); }
    finally { setBusy(null); }
  }
  async function handleConnect(provider: AuthProvider) {
    setBusy(provider);
    try { await connectProvider(provider); }     // redirects
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not start connection')); setBusy(null); }
  }
  async function handleMakePrimary(id: string, label: string) {
    setBusy(id);
    try { await setPrimaryIdentity(id); toast.success(`${label} is now your primary login method`); await load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not set primary')); }
    finally { setBusy(null); }
  }

  const unconnected = (['google', 'microsoft'] as AuthProvider[]).filter((p) => !connected.has(p));

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6">
      <div className="mb-4">
        <div className="text-sm font-semibold text-foreground">Login methods</div>
        <div className="text-xs text-muted-foreground">How you sign in — password and connected accounts</div>
      </div>

      <div className="space-y-2">
        {/* Password (a credential on the account, not an unlinkable identity) */}
        <div className="flex items-center gap-3 rounded-xl border border-border/50 px-4 py-3">
          <Password className="h-5 w-5 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-foreground">Password</div>
            <div className="text-[10px] text-muted-foreground">{data?.hasPassword ? 'Set' : 'Not set'}</div>
          </div>
          {data?.hasPassword && <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" weight="fill" />}
        </div>

        {data?.identities.map((i) => {
          const label = PROVIDER_LABEL[i.provider] ?? i.provider;
          const isLast = credentialCount <= 1;
          return (
            <div key={i.id} className="flex items-center gap-3 rounded-xl border border-border/50 px-4 py-3">
              <span className="text-foreground">{providerIcon(i.provider)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-foreground">{label}</span>
                  {i.isPrimary && <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-700 dark:text-brand-300">Primary</span>}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {i.email ?? '—'}{i.lastLoginAt ? ` · last used ${new Date(i.lastLoginAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {!i.isPrimary && i.status === 'active' && (
                  <button
                    type="button"
                    disabled={busy === i.id}
                    onClick={() => void handleMakePrimary(i.id, label)}
                    title="Make this your primary login method"
                    className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground transition hover:border-brand-200 hover:text-brand-700 dark:hover:text-brand-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Star size={11} /> Make primary
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy === i.id || isLast}
                  onClick={() => void handleUnlink(i.id, label)}
                  title={isLast ? 'You can’t remove your only sign-in method' : 'Disconnect'}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground transition hover:border-red-200 dark:border-red-500/30 hover:text-red-600 dark:hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <LinkBreak size={11} /> Disconnect
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {unconnected.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2.5 border-t border-border/50 pt-4">
          {unconnected.map((p) => (
            <button
              key={p}
              type="button"
              disabled={busy === p}
              onClick={() => void handleConnect(p)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-xs font-medium text-foreground transition hover:bg-muted/40 disabled:opacity-50"
            >
              <Plus size={13} /> {providerIcon(p)} Connect {PROVIDER_LABEL[p]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
